/**
 * Browser-only raster helpers for the export path.
 *
 * ⚠️ These deliberately do NOT live in `src/util/imageMeta.ts`. That module is
 * imported at top level by `scripts/check-image-meta.ts` under tsx (Node), so a
 * `document` or `createImageBitmap` reference there would break
 * `npm run verify:imagemeta` — a check that has nothing to do with canvases.
 */

/** Decode an image Blob (PNG/JPG/WebP) to raw RGBA via an offscreen canvas —
 *  feeds both the TIFF encoder and the GIF frame collector. */
export async function blobToImageData(blob: Blob): Promise<ImageData> {
  const bmp = await createImageBitmap(blob);
  const canvas = document.createElement("canvas");
  canvas.width = bmp.width;
  canvas.height = bmp.height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("no 2d context");
  ctx.drawImage(bmp, 0, 0);
  bmp.close();
  return ctx.getImageData(0, 0, canvas.width, canvas.height);
}

/**
 * Composite the brand watermark into an exported still.
 *
 * The watermark used to be DOM chrome floating over the iframe: visible in the
 * editor, present in exactly zero exported pixels — while being persisted into
 * render settings as though it affected output. Stills draw it for real.
 *
 * Video stays exempt (MP4/WebM are muxed inside the viewer, out of reach here),
 * which is why the control says "images".
 */
export async function drawWatermark(
  blob: Blob,
  text: string,
  mime: string,
  light: boolean,
): Promise<Blob> {
  const bmp = await createImageBitmap(blob);
  const canvas = document.createElement("canvas");
  canvas.width = bmp.width;
  canvas.height = bmp.height;
  const ctx = canvas.getContext("2d");
  if (!ctx) { bmp.close(); return blob; }
  ctx.drawImage(bmp, 0, 0);
  bmp.close();
  // Size and inset scale with the output so 1080p and 8K read the same.
  const size = Math.max(11, Math.round(canvas.height * 0.028));
  ctx.font = `600 ${size}px ui-sans-serif, system-ui, -apple-system, sans-serif`;
  ctx.textAlign = "center";
  ctx.fillStyle = light ? "rgba(255,255,255,0.5)" : "rgba(60,50,30,0.4)";
  ctx.fillText(text, canvas.width / 2, canvas.height - Math.round(canvas.height * 0.055));
  return await new Promise<Blob>((res) => canvas.toBlob((b) => res(b ?? blob), mime, 0.95));
}
